import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Avatar,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';

const ExpertDirectoryModal = ({ open, onClose, experts, onExpertSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState('all');

  const filteredExperts = experts?.filter((expert) => {
    // First filter out inactive experts
    if (expert.isActive !== 1) return false;

    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    if (searchFilter === 'name') {
      return (expert.name?.toLowerCase() || '').includes(query);
    } else if (searchFilter === 'details') {
      return (expert.position?.toLowerCase() || '').includes(query) || (expert.company?.toLowerCase() || '').includes(query);
    } else {
      // 'all'
      return (
        (expert.name?.toLowerCase() || '').includes(query) ||
        (expert.position?.toLowerCase() || '').includes(query) ||
        (expert.company?.toLowerCase() || '').includes(query)
      );
    }
  });

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setSearchFilter(newFilter);
    }
  };

  // Function to highlight matched text
  const highlightMatch = (text, query) => {
    if (!text || !query.trim()) {
      return text || '';
    }

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} style={{ backgroundColor: '#FFEB3B', fontWeight: 'bold' }}>
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="expert-directory-modal">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: '80%',
          backgroundColor: 'white',
          padding: 2,
          overflowY: 'auto',
          borderRadius: 1,
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Expert Directory
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10 }} aria-label="close">
          <Close />
        </IconButton>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <ToggleButtonGroup
            value={searchFilter}
            exclusive
            onChange={handleFilterChange}
            aria-label="search filter"
            size="small"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid rgb(228, 228, 228)',
                '&.Mui-selected': {
                  backgroundColor: '#1C7CE2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1E87C8',
                  },
                },
              },
            }}
          >
            <ToggleButton value="all" aria-label="search all">
              All
            </ToggleButton>
            <ToggleButton value="name" aria-label="search by name">
              Name
            </ToggleButton>
            <ToggleButton value="details" aria-label="search by details">
              Position & Company
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={`Search experts ${
              searchFilter === 'name' ? 'by name' : searchFilter === 'details' ? 'by position or company' : 'by name, position, or company'
            }`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Grid container spacing={1}>
          {filteredExperts?.length > 0 ? (
            filteredExperts.map((expert) => (
              <Grid item xs={12} sm={6} md={4} key={expert.id}>
                <Card
                  onClick={() => onExpertSelect(expert.id)}
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                      border: '1px solid #1C7CE2',
                    },
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <CardContent
                    sx={{
                      flex: '1 0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Avatar src={expert.imageURL} sx={{ width: '64px', height: '64px', mr: 2, flexShrink: 0 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 0.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {highlightMatch(expert.name, searchFilter === 'name' || searchFilter === 'all' ? searchQuery : '')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            wordBreak: 'break-word',
                          }}
                        >
                          {highlightMatch(expert.position, searchFilter === 'details' || searchFilter === 'all' ? searchQuery : '')} at{' '}
                          {highlightMatch(expert.company, searchFilter === 'details' || searchFilter === 'all' ? searchQuery : '')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" align="center">
                No experts found matching your search criteria
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Modal>
  );
};

ExpertDirectoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  experts: PropTypes.array.isRequired,
  onExpertSelect: PropTypes.func.isRequired,
};

export default ExpertDirectoryModal;
